import React from 'react';
import { formatDistanceToNow } from 'date-fns';

interface TrialProgressBarProps {
  trialEndsAt: string;
  showLabel?: boolean;
}

export const TrialProgressBar: React.FC<TrialProgressBarProps> = ({ trialEndsAt, showLabel = true }) => {
  const endDate = new Date(trialEndsAt);
  const now = new Date();
  const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const totalDays = 14; // Assuming 14-day trial
  const progress = Math.max(0, Math.min(100, ((totalDays - daysRemaining) / totalDays) * 100));

  const getColorClass = () => {
    if (daysRemaining <= 0) return 'bg-red-500';
    if (daysRemaining <= 3) return 'bg-orange-500';
    if (daysRemaining <= 7) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getTextColorClass = () => {
    if (daysRemaining <= 0) return 'text-red-600 dark:text-red-400';
    if (daysRemaining <= 3) return 'text-orange-600 dark:text-orange-400';
    if (daysRemaining <= 7) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  return (
    <div className="w-full">
      {showLabel && (
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Trial Progress</span>
          <span className={`text-sm font-medium ${getTextColorClass()}`}>
            {daysRemaining > 0 ? `${daysRemaining} days left` : 'Expired'}
          </span>
        </div>
      )}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full transition-all duration-300 ${getColorClass()}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      {showLabel && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Ends {formatDistanceToNow(endDate, { addSuffix: true })}
        </p>
      )}
    </div>
  );
};
